import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'
import { initials } from '@/lib/format'

/**
 * The shell's interface primitives.
 *
 * Deliberately small and unstyled-by-default: one indigo accent, a neutral
 * ramp, generous spacing, and a single focus treatment inherited from the
 * `:focus-visible` rule in index.css rather than re-declared per component.
 */

/** Panel surface, used by every card and popover so they agree in both themes. */
export const surfaceClass =
  'rounded-card border border-ink-200 bg-white dark:border-ink-800 dark:bg-ink-900'

export const mutedTextClass = 'text-ink-500 dark:text-ink-400'

/* ---------------------------------------------------------------------------
 * Button
 * ------------------------------------------------------------------------ */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800',
  secondary:
    'border border-ink-200 bg-white text-ink-800 hover:bg-ink-50 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100 dark:hover:bg-ink-800',
  ghost:
    'text-ink-600 hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
}

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-sm',
  md: 'h-10 gap-2 px-4 text-sm',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner and disables the button. */
  loading?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium whitespace-nowrap transition',
        'disabled:pointer-events-none disabled:opacity-55',
        BUTTON_SIZES[size],
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading ? <Spinner className="size-4" /> : null}
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn('size-5 animate-spin', className)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

/* ---------------------------------------------------------------------------
 * Form fields
 * ------------------------------------------------------------------------ */

/** The shared input/select skin. Exported so composed fields (the auth
 *  screens' password input, which carries a show/hide button inside the
 *  border) can reuse it instead of restating it. */
export const controlClass =
  // Nothing here suppresses the outline. Tailwind's outline-suppressing utility
  // is emitted into @layer utilities, which is ordered after @layer base, so it
  // beats the one `:focus-visible` rule in index.css and would leave every text
  // field with a hairline border change as its only focus indicator while every
  // button around it kept the 2px ring. (Written out rather than quoted: the
  // scanner reads comments too, and would emit the class from this line alone.)
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 transition ' +
  'placeholder:text-ink-400 hover:border-ink-300 focus:border-brand-500 ' +
  'disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-500 ' +
  'aria-[invalid=true]:border-red-500 ' +
  'dark:border-ink-700 dark:bg-ink-950 dark:text-ink-100 dark:hover:border-ink-600 dark:disabled:bg-ink-900'

export interface FieldShellProps {
  label: string
  hint?: ReactNode
  error?: string
  /** Renders the label for screen readers only. */
  hideLabel?: boolean
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
}

/** Label + hint + error scaffolding, wired to the control with aria-describedby. */
export function FieldShell({ label, hint, error, hideLabel, children }: FieldShellProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = error ? errorId : hint ? hintId : undefined

  return (
    <div>
      <label
        htmlFor={id}
        className={cn(
          'mb-1.5 block text-sm font-medium',
          hideLabel && 'sr-only',
        )}
      >
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {error ? (
        <p id={errorId} className="mt-1.5 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className={cn('mt-1.5 text-xs', mutedTextClass)}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string
  hideLabel?: boolean
}

export function TextField({ label, hint, error, hideLabel, className, ...rest }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} hideLabel={hideLabel}>
      {({ id, describedBy, invalid }) => (
        <input
          {...rest}
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(controlClass, className)}
        />
      )}
    </FieldShell>
  )
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string
  hideLabel?: boolean
}

export function SelectField({
  label,
  hint,
  error,
  hideLabel,
  className,
  children,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} hideLabel={hideLabel}>
      {({ id, describedBy, invalid }) => (
        <select
          {...rest}
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(controlClass, 'pr-8', className)}
        >
          {children}
        </select>
      )}
    </FieldShell>
  )
}

/* ---------------------------------------------------------------------------
 * Feedback
 * ------------------------------------------------------------------------ */

export type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const ALERT_TONES: Record<AlertTone, string> = {
  info: 'border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-900 dark:bg-brand-950 dark:text-brand-100',
  success:
    'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  warning:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100',
  danger: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100',
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: AlertTone
  title?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('rounded-card border px-4 py-3 text-sm', ALERT_TONES[tone], className)}
    >
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? (
        <div className={cn(title ? 'mt-1' : null, 'text-[13px] leading-relaxed')}>{children}</div>
      ) : null}
    </div>
  )
}

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger'

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300',
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  danger: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('px-6 py-12 text-center', className)}>
      <p className="text-sm font-medium">{title}</p>
      {description ? (
        <p className={cn('mx-auto mt-1 max-w-sm text-sm', mutedTextClass)}>{description}</p>
      ) : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  )
}

/** Placeholder rows sized like the content they stand in for. */
export function SkeletonRows({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-10 animate-pulse rounded-lg bg-ink-100 dark:bg-ink-800" />
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Structure
 * ------------------------------------------------------------------------ */

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn(surfaceClass, className)}>{children}</section>
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 px-5 py-4 dark:border-ink-800">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {description ? <p className={cn('mt-0.5 text-sm', mutedTextClass)}>{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className={cn('mt-1 text-sm', mutedTextClass)}>{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/**
 * Avatar palette.
 *
 * `users.avatar_color` holds a palette NAME — "sky", "amber" — not a CSS
 * colour, so it has to be mapped rather than passed through to a style
 * attribute, where it would silently produce a transparent circle. The names
 * are the eight the server picks from; anything unrecognised falls back to the
 * brand indigo. Every class is written out in full because Tailwind scans
 * source text and cannot see an interpolated class name.
 */
const AVATAR_COLORS: Record<string, string> = {
  indigo: 'bg-brand-600',
  violet: 'bg-violet-600',
  sky: 'bg-sky-600',
  teal: 'bg-teal-600',
  amber: 'bg-amber-600',
  rose: 'bg-rose-600',
  emerald: 'bg-emerald-600',
  slate: 'bg-slate-600',
}

/** A round, letter-based avatar. The colour is stable per person, server-side. */
export function Avatar({
  name,
  color,
  size = 'md',
  className,
}: {
  name: string
  color?: string | null
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const sizes = { sm: 'size-6 text-[10px]', md: 'size-8 text-xs', lg: 'size-10 text-sm' }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        (color && AVATAR_COLORS[color]) || 'bg-brand-600',
        sizes[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}

/** A labelled usage bar. `limit` below zero means no cap. */
export function Meter({
  label,
  used,
  limit,
  formatValue = (value: number) => String(value),
}: {
  label: string
  used: number
  limit: number
  formatValue?: (value: number) => string
}) {
  // The API expresses "no cap" as 0 — model.Limits treats a zero as unlimited
  // and every paid tier is unlimited on projects, issues and notes. Rendering
  // that as "12 / 0" was the old behaviour and reads as a broken meter.
  const unlimited = limit <= 0
  const ratio = unlimited ? 0 : Math.min(1, used / limit)
  const tone = ratio >= 1 ? 'bg-red-500' : ratio >= 0.8 ? 'bg-amber-500' : 'bg-brand-600'

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className={cn('tabular-nums', mutedTextClass)}>
          {formatValue(used)}
          {unlimited ? '' : ` / ${formatValue(limit)}`}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={unlimited ? undefined : limit}
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-100 dark:bg-ink-800"
      >
        <div
          className={cn('h-full rounded-full transition-[width]', unlimited ? 'bg-brand-300' : tone)}
          style={{ width: unlimited ? '100%' : `${Math.max(ratio * 100, used > 0 ? 4 : 0)}%` }}
        />
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Overlays
 * ------------------------------------------------------------------------ */

/**
 * Closes an overlay on an outside pointer press or Escape.
 *
 * Pointerdown rather than click: a click listener fires after the button that
 * opened the menu has already toggled it, which makes the menu impossible to
 * close by clicking its own trigger.
 */
export function useDismissable(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose()
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  return ref
}

export function MenuItem({
  children,
  onClick,
  danger,
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition',
        'disabled:pointer-events-none disabled:opacity-50',
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950'
          : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800',
      )}
    >
      {children}
    </button>
  )
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  // Move focus into the dialog so the keyboard does not stay behind the
  // backdrop, and restore it to the trigger when the dialog closes.
  useLayoutEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const target = panelRef.current?.querySelector<HTMLElement>(
      'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])',
    )
    target?.focus()
    return () => previous?.focus?.()
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div
        className="absolute inset-0 bg-ink-950/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          surfaceClass,
          'relative w-full max-w-md p-5 shadow-xl shadow-ink-950/10',
        )}
      >
        <h2 id={titleId} className="text-base font-semibold tracking-tight">
          {title}
        </h2>
        {description ? <p className={cn('mt-1 text-sm', mutedTextClass)}>{description}</p> : null}
        {children ? <div className="mt-4">{children}</div> : null}
        {footer ? <div className="mt-6 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>
  )
}

/**
 * A confirmation dialog for destructive actions.
 *
 * `confirmWord` makes the irreversible cases (deleting a workspace) require the
 * name to be typed, which is the difference between a mis-click and an intent.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmWord,
  loading,
  error,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description?: ReactNode
  confirmLabel?: string
  confirmWord?: string
  loading?: boolean
  error?: string | null
}) {
  const [typed, setTyped] = useState('')

  useEffect(() => {
    if (!open) setTyped('')
  }, [open])

  const armed = !confirmWord || typed.trim() === confirmWord

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={!armed} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {confirmWord ? (
        <TextField
          label={`Type “${confirmWord}” to confirm`}
          value={typed}
          autoComplete="off"
          onChange={(event) => setTyped(event.target.value)}
        />
      ) : null}
      {error ? (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}
